import React, { useState, useEffect } from 'react';
import { Card, Table, Button, Alert, Form } from 'react-bootstrap';
import { getRolePermissions, assignPermissionToRole, removePermissionFromRole } from '../services/api/roleApi';
import { getAllPermissions } from '../services/api/permissionsApi';

const RolePermissions = ({ role, onBack }) => {
  const [rolePermissions, setRolePermissions] = useState([]);
  const [availablePermissions, setAvailablePermissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedPermission, setSelectedPermission] = useState('');

  useEffect(() => {
    fetchData();
  }, [role]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [rolePermsResponse, allPermsResponse] = await Promise.all([
        getRolePermissions(role.id),
        getAllPermissions()
      ]);
      
      setRolePermissions(rolePermsResponse.data);
      setAvailablePermissions(allPermsResponse.data);
    } catch (error) {
      setError('Failed to fetch permissions data');
    } finally {
      setLoading(false);
    }
  };

  const handleAddPermission = async () => {
    if (!selectedPermission) return;
    
    try {
      await assignPermissionToRole(role.id, selectedPermission);
      fetchData();
      setSelectedPermission('');
    } catch (error) {
      setError('Failed to assign permission to role');
    }
  };

  const handleRemovePermission = async (permissionId) => {
    try {
      await removePermissionFromRole(role.id, permissionId);
      fetchData();
    } catch (error) {
      setError('Failed to remove permission from role');
    }
  };

  const getUnassignedPermissions = () => {
    const assignedIds = new Set(rolePermissions.map(p => p.id));
    return availablePermissions.filter(p => !assignedIds.has(p.id));
  };

  if (loading) return <div>Loading permissions...</div>;

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h2>Manage Permissions for {role.name}</h2>
        <Button variant="secondary" onClick={onBack}>
          Back to Roles
        </Button>
      </div>

      {error && <Alert variant="danger">{error}</Alert>}

      <Card className="mb-4">
        <Card.Header>Add Permission</Card.Header>
        <Card.Body>
          <div className="d-flex">
            <Form.Select 
              value={selectedPermission} 
              onChange={(e) => setSelectedPermission(e.target.value)}
              className="me-2"
            >
              <option value="">Select a permission</option>
              {getUnassignedPermissions().map(permission => (
                <option key={permission.id} value={permission.id}>
                  {permission.action}:{permission.resource}
                </option>
              ))}
            </Form.Select>
            <Button 
              variant="primary" 
              onClick={handleAddPermission}
              disabled={!selectedPermission}
            >
              Add Permission
            </Button>
          </div>
        </Card.Body>
      </Card>

      <Card>
        <Card.Header>Current Permissions</Card.Header>
        <Card.Body>
          <Table striped bordered hover>
            <thead>
              <tr>
                <th>Action</th>
                <th>Resource</th>
                <th>Description</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {rolePermissions.length === 0 ? (
                <tr>
                  <td colSpan="4" className="text-center">No permissions assigned to this role</td>
                </tr>
              ) : (
                rolePermissions.map(permission => (
                  <tr key={permission.id}>
                    <td>{permission.action}</td>
                    <td>{permission.resource}</td>
                    <td>{permission.description}</td>
                    <td>
                      <Button
                        variant="outline-danger"
                        size="sm"
                        onClick={() => handleRemovePermission(permission.id)}
                      >
                        Remove
                      </Button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>
        </Card.Body>
      </Card>
    </div>
  );
};

export default RolePermissions;